import env from 'env'

import { Api } from '.'

import { HTTP_METHOD, PLATFORM_TYPE } from '../../constants/enums'

import { dateToUTCStamp } from '../../utils/time'
import { fixCDNBaseUrl } from '../../utils'
import { livephishLegacyParams, type ArtistContentItem } from '../../utils/data'

import type { BaseSDK } from '../../sdk'

export class CatalogApi extends Api {
  origin = env.CATALOG_API_BASE
  version = 'v1'

  async fetchReleaseItem(id: string | number): Promise<Nullable<LegacyReleaseResponse>> {
    try {
      if (id == null) return null

      const response = await this.fetch(`shows/${+id}`, HTTP_METHOD.GET)

      if (response.ok) {
        const payload: { Response: LegacyReleaseResponse } = await response.json()
        return payload?.Response || null
      } else return null
    } catch {
      return null
    }
  }

  /**
   * TODO: Move to service worker to cache stream links automatically when legacy api will be removed/changed
   * @param id
   * @param platform
   * @returns
   */
  async fetchStreamLink(id: number, platform: PLATFORM_TYPE = PLATFORM_TYPE.BROWSER, sdk: BaseSDK) {
    if (id == null) return undefined
    const account = sdk.account

    if (!account.isSubscribed && account.subscription.isContentAccessible) return null

    const quality = platform !== PLATFORM_TYPE.UNKNOWN ? 1 : 0
    const format = 'MM/dd/yyyy HH:mm:ss'
    const startDateStamp = dateToUTCStamp(account.subscription.startedAt as string, format)
    const endDateStamp = dateToUTCStamp(account.subscription.endsAt as string, format)

    const query = {
      platformID: platform,
      app: +true,
      HLS: +true,
      orgn: 'websdk',
      method: 'subPlayer',
      trackId: +id,
      subCostplanIDAccessList: account.subscription.plan as string,
      startDateStamp,
      endDateStamp,
      nn_userID: account.id as string,
      subscriptionID: account.subscription.legacyId as string,
    }

    if (env.OIDC_AUTHORITY.includes('livephish')) {
      // LivePhish legacy token
      // LivePhish legacy param
      const { tk, lxp } = livephishLegacyParams()
      Object.assign(query, { tk, lxp })
    }

    try {
      const { streamLink } = await this.fetchJSONP<{ streamLink: string | null }>(env.LEGACY_PLAYER_URL_BASE, query)

      return streamLink ? { id, quality, date: new Date, data: fixCDNBaseUrl(streamLink) } : undefined as never
    } catch (error) {
      console.error(error)
      return undefined as never
    }
  }
}

/** * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *
 * Catalog Api provides pretty different structure for releases.
 * There is few types of releases:
 *
 * @description Catalog Api types
 *
* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

type CatalogReleaseTypeA = {
  addedAt?: string
  albumTitle: Nullable<string>
  albumAbbreviation: Nullable<string>
  albumReleaseDate: Nullable<string>
  hasVideoOnDemand: boolean
  audioFormatTypes: string[]
  videoFormatTypes: string[]
  id: string
  status: string
  type: string
  title: string
  performanceDate: Nullable<string>
  coverImage: Nullable<string | { url: Nullable<string> }>
  image: Nullable<{
    url: Nullable<string>
    width?: Nullable<number>
    height?: Nullable<number>
  }>
  venue: Nullable<{
    id: string
    title: string
    name: string
    city: string
    state: string
    country: string
  }>
  artist: {
    abbreviation: string
    id: string
    name: string
  }
  isInSubscriptionCatalog: boolean
}

type CatalogReleaseTypeB = {
  id: string
  availabilityType: string
  releaseType: string
  hasVideoContent: boolean
  isInSubscriptionProgram: boolean
  audioFormatTypes: string[]
  videoFormatTypes: string[]
  coverImage: {
    id: Nullable<string>
    url: Nullable<string>
  }
  artist: {
    id: string
    name: string
  }
  showDetails: {
    venue: {
      id: string
      title: string
      name: string
      city: string
      state: string
      country: string
    }
    performanceDate: string
  }
  albumDetails: Nullable<{
    albumAbbr: string
    albumTitle: string
    releaseDate: string
  }>
}

type CatalogReleaseTypeC = {
  id: string
  title: string
  format: string
  duration: number
  release: {
    id: string
    availabilityType: string
    releaseType: string
    hasVideoContent: boolean
    isInSubscriptionProgram: boolean
    audioFormatTypes: Nullable<string[]>
    videoFormatTypes: Nullable<string[]>
    coverImage: {
      id: Nullable<string>
      url: Nullable<string>
    }
    artist: {
      id: string
      name: string
    }
    showDetails: {
      venue: {
        id: string
        title: string
        name: string
        city: string
        state: string
        country: string
      }
      performanceDate: string
    }
    albumDetails: Nullable<{
      albumAbbr: string
      albumTitle: string
      releaseDate: string
    }>
  }
}

type CatalogReleaseTypeD = {
  data: CatalogReleaseTypeA | ArtistContentItem
  type: 'artist' | 'release'
}


type CatalogReleaseTypeE = CatalogReleaseTypeB & {
  priority: number
}

type PromoTray = {
  id: string
  title: string
  subTitle: string
  releases: CatalogReleaseTypeE[]
  promoType: string
}

type CatalogReleaseItem = CatalogReleaseTypeA & CatalogReleaseTypeB & CatalogReleaseTypeC & CatalogReleaseTypeD & CatalogReleaseTypeE

export type {
  CatalogReleaseTypeA,
  CatalogReleaseTypeB,
  CatalogReleaseTypeC,
  CatalogReleaseTypeD,
  CatalogReleaseTypeE,
  CatalogReleaseItem,
  PromoTray
}

/** * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *
 * Legacy database show/release structure
 *
 * @description Legacy types
 *
* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

type LegacyReview = {
  review: string
  reviewID: number
  reviewDate: string
  reviewStatus: number
  reviewerName: string
}

type LegacyNote = {
  note: string
  noteID: number
}

type LegacyEventInfo = {
  eventID: number | null
  isEventLive: boolean
  eventStartDateStr: string | null
  eventEndDateStr: string | null
  timeZoneToDisplay: string | null
}

type LegacyEventSaleInfo = {
  saleEndDateStr: string | null
  saleStartDateStr: string | null
  timeZoneToDisplay: string | null
}

/** `pfType` legacy property
    MP3 = 0
    LOSSLESS = 1
    HD = 2
    VIDEO = 3
    LIVEVIDEO = 4
    MP4 = 5
    VIDEOONDEMAND = 6
    CD = 7
    LIVEHDVIDEO = 8
    HDVIDEOONDEMAND = 9
    HDVIDEO = 10
*/

type pfType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

type LegacyProduct = {
  productStatusType: number,
  isFreeAudioLivestream: number
  isSubStreamOnly: number
  skuID: number
  pfType: pfType
  formatStr: string
  liveEventInfo: LegacyEventInfo
  liveEvent: LegacyEventInfo
  salewindow: LegacyEventSaleInfo
}

type LegacyTrack = {
  setNum: number,
  trackID: number,
  songID: number,
  trackNum: number,
  discNum: number,
  songTitle: string,
  totalRunningTime: number
  liveHDstreamProduct: boolean | null
  livestreamProduct: boolean | null
  mp4Product: boolean | null
  reality360Product: boolean | null
  clipURL: string
}

type LegacyArtist = {
  pageURL: string
  artistName: string
  artistNameNoThe: string
  artistImage?: string
  artistID: string | number
  numAlbums: number
  numShows: number
}

type LegacySearchResultItem = {
  containerName: string
  performanceDate: string
  artistID: string
  artistName: string
  clipURL: null
  pageURL: string
  containerID: number
  trackID: number
  songID: number
  img: {
    url: string | undefined
  }
  venueCity: string
  venueState: string
  venueName: string
  venue: string
  availability: number
  availabilityStr: string
}

type LegacyCatalogSearchContainer = {
  catalogSearchResultItems: Array<LegacySearchResultItem>
  matchedStr: string
}

type LegacyCatalogSearchTypeContainers = {
  catalogSearchContainers: Array<LegacyCatalogSearchContainer>
  matchType: number
}

type LegacyCatalogSearchResponse = {
  Response: {
    catalogSearchTypeContainers: Array<LegacyCatalogSearchTypeContainers>
  }
}

type LegacyRecommendedRelease = {
  containerID: number
  picURL: string
  venue: string
  titleName: string
  containerType: number
  containerTypeStr: 'Show' | 'Album'
  pageURL: string
  artistName: string
  venueCity: string
  venueState: string
  venueName: string
  performanceDateShort: string,
}

type LegacyPlaylist<T> = {
  ID: number
  alreadyExistsFlag: boolean
  createDate: string
  generatedGUID: null | string
  hhmmssTotalRunningTime: null | string
  items: null | T[]
  numTracks: number
  playListName: string
  playListUserInvalid: boolean
  playlistImage: string
  shortenedLink: null | string
  totalRunningTime: number
  userID: number
}

export interface LegacyReleaseResponse {
  containerID: string
  artistName: string
  artistID: string
  performanceDateFormatted: string
  performanceDate: string
  releaseDate: string
  licensorName: string
  venue: string
  venueName: string
  venueCity: string
  venueState: string
  name: string
  title: string
  extImage: string
  img: Record<string, string>
  hhmmssTotalRunningTime: string
  totalContainerRunningTime: number
  isInSubscriptionProgram: boolean
  notes: LegacyNote[]
  tracks: LegacyTrack[]
  containerType: number
  prevContainerID: number | null
  nextContainerID: number | null
  epochDateCreated: number
  hasSony360VOD: boolean
  containerInfo?: string
  svodskuID: number
  activeState: string
  products: Array<LegacyProduct>
  productFormatList: Array<LegacyProduct>
  videoChapters: Array<{ chaptername: string, chapterSeconds: number }>
  recommendations: Array<LegacyRecommendedRelease>
  reviews: {
    items: LegacyReview[],
    pageNum: number
    totalPages: number
    totalRecords: number
    numPerPage: number
  }
}

export type {
  LegacyCatalogSearchContainer,
  LegacyCatalogSearchTypeContainers,
  LegacyCatalogSearchResponse,
  LegacyReview,
  LegacyRecommendedRelease,
  LegacyNote,
  LegacyEventInfo,
  LegacyEventSaleInfo,
  LegacyProduct,
  LegacyTrack,
  LegacyPlaylist,
  LegacySearchResultItem,
  LegacyArtist
}
